<template>
  <base-page-layout title="Lojas" class="lojas">
    <div class="d-flex align-center justify-space-between">
      <div class="d-flex align-center">
        <mf-tabs-button v-model="filterType" :buttons="toggleButtons" />
        <v-select v-model="selectedIndicator" outlined hide-details class="indicadores-select mx-4" :items="indicators" label="Indicadores" dense />
        <v-select v-model="selectedYear" dense outlined hide-details class="year-select mr-4" :items="years" label="Ano" />
        <mf-select-units-dialog
          class="mf-select-units"
          label="Lojas"
          hide-details
          :selected-units.sync="selectedUnits"
          :selected-units-configuration.sync="selectedUnitsConfiguration"
          check-all-units-on-init
          :units="units"
          :tag-list="tagList"
          :group-list="unitsGroups"
          enable-inactive-units-selection
          dense
        />
      </div>
      <div class="pl-4">
        <export-data :data-fetcher="getDataToExport" :filename="`clube_lojas_${selectedIndicatorData.text}`" />
      </div>
    </div>
    <div class="mt-3 mf-card">
      <mf-loading :boolean-loader="loading" loader-type="table">
        <v-data-table
          v-if="filteredItemsByYear && filteredItemsByYear.length"
          :class="['mt-4', 'sticky-table', filterByGroups ? 'expandable-table' : 'unexpandable-table']"
          :items="sortedItems"
          :headers="headers"
          :height="calcTableHeight"
          fixed-header
          hide-default-footer
          disable-pagination
          no-data-text="Não há dados a serem exibidos"
          :expanded.sync="expanded"
          :single-expand="false"
          item-key="group_id"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
        >
          <template v-slot:item="{ item, expand, isExpanded }">
            <tr :class="isUnitDisabled(item.group_id) ? 'disabled-tr' : ''">
              <td v-if="filterByGroups" class="expand-td">
                <mf-button color="marca-1" :icon="isExpanded ? 'indeterminate_check_box' : 'add_box'" type="icon" size="sm" @click="expand(!isExpanded)" />
              </td>

              <td :class="filterByGroups ? 'expandable-td' : 'unexpandable-td'">
                <div class="ellipsis-container d-flex align-center">
                  <mf-tooltip max-width="250" top>
                    <template v-slot:activator>
                      <div class="hidden">hidden</div>
                      <span v-if="filterByGroups" class="ellipsis-td link" @click="goToCreateGroup(true)">
                        {{ item.name }}
                      </span>

                      <span v-else class="ellipsis-td">
                        {{ item.name }}
                      </span>
                    </template>
                    <span> {{ item.name }}</span>
                  </mf-tooltip>
                </div>
              </td>

              <td v-if="!filterByFranchisee">
                <mf-tooltip v-if="filterByGroups" top max-width="200px">
                  <template v-slot:activator="{ on }">
                    <span v-on="on">{{ item.units_metrics.length === 1 ? `1 CNPJ` : `${item.units_metrics.length} CNPJs` }}</span>
                  </template>
                  <div v-for="unitMetrics in item.units_metrics" :key="unitMetrics.cnpj">
                    <span>{{ formatCnpj(unitMetrics.cnpj) }}</span>
                  </div>
                </mf-tooltip>

                <span v-else>{{ formatCnpj(item.cnpj) }}</span>
              </td>

              <td
                v-for="index in 12"
                :key="index"
                :class="{ 'hover-column': hoveredColumn === `month-${index}` }"
                @mouseenter="hoveredColumn = `month-${index}`"
                @mouseleave="hoveredColumn = null"
              >
                <span v-if="selectedIndicatorData.currency && item.metrics[index - 1] && item.metrics[index - 1][selectedIndicator]">R$ </span>
                <span v-if="!item.metrics[index - 1] || !item.metrics[index - 1][selectedIndicator]">-</span>
                <span v-else>{{
                  item.metrics[index - 1] && item.metrics[index - 1][selectedIndicator] | localeString(selectedIndicatorData.digits || 0)
                }}</span>
                <span v-if="selectedIndicatorData.percent && item.metrics[index - 1] && item.metrics[index - 1][selectedIndicator]">%</span>
              </td>
            </tr>
          </template>

          <!-- Units groups expanded item -->
          <template v-slot:expanded-item="{ item }">
            <tr v-for="unitMetrics in item.units_metrics" :key="unitMetrics.cnpj">
              <td></td>
              <td>{{ unitMetrics.name }}</td>
              <td>{{ formatCnpj(unitMetrics.cnpj) }}</td>
              <td v-for="index in 12" :key="index">
                <span v-if="selectedIndicatorData.currency && unitMetrics.metrics[index - 1] && unitMetrics.metrics[index - 1][selectedIndicator]">R$ </span>
                <span v-if="!unitMetrics.metrics[index - 1] || !unitMetrics.metrics[index - 1][selectedIndicator]">-</span>
                <span v-else>{{
                  unitMetrics.metrics[index - 1] &&
                  unitMetrics.metrics[index - 1][selectedIndicator] | localeString(selectedIndicatorData.currency ? 2 : selectedIndicatorData.percent ? 2 : 0)
                }}</span>
                <span v-if="selectedIndicatorData.percent && unitMetrics.metrics[index - 1] && unitMetrics.metrics[index - 1][selectedIndicator]">%</span>
              </td>
            </tr>
          </template>
        </v-data-table>

        <div v-else-if="filterByGroups" class="no-groups-container d-flex justify-center align-center mt-4">
          <div class="no-groups-content">
            <v-img :src="require('@/assets/unitsGroupsEmptyState.svg')" />
            <p>Você ainda não possui nenhum grupo criado. Acesse as configurações das suas lojas para começar a criar.</p>
            <mf-button label="Criar grupo" class="unset-capitalize" color="marca-0-l10" @click="goToCreateGroup" />
          </div>
        </div>
      </mf-loading>
    </div>
  </base-page-layout>
</template>

<script>
import capitalize from 'lodash/capitalize'
import isEqual from 'lodash/isEqual'
import BasePageLayout from '@/components/BasePageLayout.vue'
import { putEllipsis } from '@/config/helpers.js'
import { mapState, mapActions, mapGetters } from 'vuex'
import Info from 'luxon/src/info.js'
import cnpjFilter from '@/filters/cnpj.js'

const MOST_YEARS_AGO = 10

export default {
  name: 'Lojas',
  components: {
    BasePageLayout,
    ExportData: () => import('@/components/export-data/ExportData.vue'),
  },
  filters: {
    cnpj: cnpjFilter,
  },
  data: () => ({
    filterType: 'units',
    selectedUnits: [],
    selectedUnitsConfiguration: [],
    selectedIndicator: 'loyalty_revenue_penetration',
    selectedYear: '',
    loading: false,
    timeout: null,
    years: [],
    expanded: [],
    hoveredColumn: null,
    sortBy: '',
    sortDesc: false,
  }),
  computed: {
    ...mapGetters(['units', 'tagList', 'getMetricsUnitsComparision', 'unitsGroups']),
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
      stateFilters: (state) => state.filters,
      allUnits: (state) =>
        state.units.units.sort((a, b) => {
          if (a.active && !b.active) return -1
          else if (!a.active && b.active) return 1
          return 0
        }),
    }),
    calcTableHeight() {
      const lineHeight = 48
      const maxLines = 11
      const scollPadding = 10
      let linesToShow = 0
      if (this.filterByGroups) {
        linesToShow += this.sortedItems.length
        linesToShow += this.expanded.reduce((acc, el) => acc + (el.units_metrics?.length ?? 0), 0)
      } else if (this.filterByFranchisee) {
        linesToShow += this.sortedItems.length
      } else {
        linesToShow = Math.min(maxLines, this.allUnits?.length ?? 1)
      }
      linesToShow = Math.min(maxLines, linesToShow)
      const height = lineHeight * (linesToShow + 1) + scollPadding
      return `${height}px`
    },
    toggleButtons() {
      return [
        {
          id: 0,
          label: 'Lojas',
          value: 'units',
          active: true,
        },
        {
          id: 1,
          label: 'Grupos',
          value: 'groups',
          active: true,
        },
        ...(this.isFranchise
          ? [
              {
                id: 2,
                label: 'Franqueados',
                value: 'franchisee',
                active: true,
              },
            ]
          : []),
      ]
    },
    filterByGroups() {
      return this.filterType === 'groups'
    },
    filterByFranchisee() {
      return this.filterType === 'franchisee'
    },
    filterByUnits() {
      return this.filterType === 'units'
    },
    isFranchise() {
      return this.$getLoggedUser().is_franchise
    },
    indicators() {
      return [
        {
          text: 'Percentual de faturamento fidelidade',
          value: 'loyalty_revenue_penetration',
          digits: 2,
          percent: true,
          currency: false,
        },
        {
          text: 'Percentual de cupons fidelidade',
          value: 'loyalty_coupon_penetration',
          digits: 2,
          percent: true,
          currency: false,
        },
        {
          text: 'Clientes ativos no clube',
          value: 'clients',
          percent: false,
          currency: false,
        },
        {
          text: 'Loja da primeira compra do cliente',
          value: 'loyalty_clients_first_purchase',
          percent: false,
          currency: false,
        },
        {
          text: 'Faturamento no clube',
          value: 'loyalty_revenue',
          digits: 2,
          currency: true,
          percent: false,
        },
        {
          text: 'Total de faturamento',
          value: 'revenue',
          digits: 2,
          currency: true,
          percent: false,
        },
        {
          text: 'Cupons no clube',
          value: 'loyalty_coupons',
          percent: false,
          currency: false,
        },
        {
          text: 'Total de cupons',
          value: 'coupons',
          percent: false,
          currency: false,
        },
        {
          text: 'Ticket médio no clube',
          value: 'loyalty_average_ticket',
          digits: 2,
          currency: true,
          percent: false,
        },
        {
          text: 'Ticket médio geral',
          value: 'average_ticket',
          digits: 2,
          currency: true,
          percent: false,
        },
        {
          text: 'Média de itens na cesta no clube',
          value: 'loyalty_average_items_in_basket',
          digits: 2,
          currency: false,
          percent: false,
        },
        {
          text: 'Média de itens na cesta geral',
          value: 'average_items_in_basket',
          digits: 2,
          currency: false,
          percent: false,
        },
        {
          text: 'Frequência média de compras no clube',
          value: 'loyalty_average_frequency',
          digits: 2,
          currency: false,
          percent: false,
        },
        {
          text: 'Gasto médio no clube',
          value: 'loyalty_average_spend',
          digits: 2,
          currency: true,
          percent: false,
        },
      ]
    },
    selectedIndicatorData() {
      return this.indicators.find((indicator) => indicator.value === this.selectedIndicator)
    },
    headers() {
      let monthsArray = Info.months('long', { locale: 'pt-BR' })
      monthsArray = monthsArray.map((month, index) => {
        return {
          text: capitalize(month),
          sortable: true,
          align: 'start',
          value: String(index + 1),
          width: '130px',
        }
      })
      if (!this.filterByFranchisee) {
        monthsArray.unshift({
          text: 'CNPJ',
          value: 'cnpj',
          sortable: true,
          width: '172px',
        })
      }
      monthsArray.unshift({
        text: 'Nome',
        value: 'name',
        align: 'start',
        sortable: true,
        width: '250px',
      })

      if (this.filterByGroups) {
        monthsArray.unshift({
          text: '',
          value: '',
          sortable: false,
          width: '50px',
        })
      }

      return monthsArray
    },
    items() {
      if (this.loading || !this.getMetricsUnitsComparision.length || !this.selectedYear) return []

      const allMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
      const years = this.years
      const filteredItems = this.getMetricsUnitsComparision.map((item) => {
        const filledMetrics = this.fillMetrics(item?.metrics, years, allMonths)

        if (item?.units_metrics) {
          const unitsMetrics = Object.keys(item.units_metrics).map((unitId) => {
            const unitMetricItem = item.units_metrics[unitId]
            const filledUnitMetrics = this.fillMetrics(unitMetricItem.metrics, years, allMonths)
            return { ...unitMetricItem, metrics: filledUnitMetrics }
          })
          return { ...item, metrics: filledMetrics, units_metrics: unitsMetrics }
        }
        return { ...item, metrics: filledMetrics }
      })

      if (this.filterByUnits) {
        return filteredItems.sort((a, b) => {
          const indexA = this.allUnits.findIndex((obj) => obj.id_erp === a.group_id)
          const indexB = this.allUnits.findIndex((obj) => obj.id_erp === b.group_id)
          return indexA - indexB
        })
      }
      return filteredItems
    },
    filteredItemsByYear() {
      if (this.loading || !this.items.length || !this.selectedYear) return []

      const filteredItems = this.items.map((item) => {
        return {
          ...item,
          metrics: item.metrics.filter((metric) => metric.year === this.selectedYear),
          units_metrics: item?.units_metrics
            ? item.units_metrics.map((unitMetric) => {
                return {
                  ...unitMetric,
                  metrics: unitMetric.metrics.filter((m) => m.year === this.selectedYear),
                }
              })
            : [],
        }
      })

      return filteredItems
    },
    sortedItems() {
      const sortBy = Array.isArray(this.sortBy) ? this.sortBy[0] : this.sortBy
      const sortDesc = Array.isArray(this.sortDesc) ? this.sortDesc[0] : this.sortDesc
      const clonedItems = [...this.filteredItemsByYear]

      if (!sortBy) return this.filteredItemsByYear
      if (['name', 'cnpj'].includes(sortBy)) {
        if (sortBy === 'cnpj' && this.filterType === 'groups') {
          const compareFn = (a, b) => (sortDesc ? b.units_metrics.length - a.units_metrics.length : a.units_metrics.length - b.units_metrics.length)
          return clonedItems.sort(compareFn)
        }

        const compareFn = (a, b) => (sortDesc ? a[sortBy].localeCompare(b[sortBy]) : b[sortBy].localeCompare(a[sortBy]))
        return clonedItems.sort(compareFn)
      }

      const compareFn = (a, b) => {
        const valueA = a.metrics[sortBy - 1][this.selectedIndicator] || 0
        const valueB = b.metrics[sortBy - 1][this.selectedIndicator] || 0
        return sortDesc ? valueB - valueA : valueA - valueB
      }

      return clonedItems.sort(compareFn)
    },
  },
  watch: {
    selectedUnits: {
      deep: true,
      handler(newVal, oldVal) {
        const isEqualArrays = isEqual(newVal, oldVal)
        if (newVal && newVal.length && !isEqualArrays) {
          this.waitAndGetData()
        }
      },
    },
    selectedYear(newVal, oldVal) {
      const isEqualYears = isEqual(newVal, oldVal)
      if (!isEqualYears) {
        this.waitAndGetData()
      }
    },
    filterType() {
      this.sortBy = ''
      this.sortDesc = false
      this.waitAndGetData()
    },
  },
  async mounted() {
    this.loading = true
    this.years = this.getLastYears()
    this.selectedYear = this.years[0]
    await this.getData()
  },
  methods: {
    putEllipsis,
    ...mapActions(['metricsUnitsComparision']),
    formatCnpj(value) {
      if (!value) return '-'
      if (typeof value !== 'string') return String(value)
      return value.replace(/[^\d]/g, '').replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
    },
    waitAndGetData() {
      clearTimeout(this.timeout)
      this.loading = true
      this.timeout = setTimeout(() => {
        this.getData()
      }, 1000)
    },
    getLastYears() {
      const currentYear = new Date().getFullYear()
      return Array.from({ length: MOST_YEARS_AGO }, (_, i) => currentYear - i)
    },
    goToCreateGroup(isNewTab = false) {
      if (isNewTab) {
        const url = `/configuracoes/units`
        const routeURL = this.$router.resolve({ path: url }).href

        window.open(routeURL, '_blank')
      } else {
        this.$router.push('/configuracoes/units')
      }
    },
    async getData() {
      const filters = {
        group_by_franchisee: this.filterByFranchisee,
        group_by_units_groups: this.filterByGroups,
        units: this.selectedUnitsConfiguration,
        year: this.selectedYear,
      }
      await this.metricsUnitsComparision(filters)
      this.loading = false

      if (!this.years.length) {
        this.setYears()
        this.selectedYear = this.years[this.years.length - 1]
      }
    },
    setYears() {
      const years = []
      if (this.loading || !this.getMetricsUnitsComparision.length) return
      this.getMetricsUnitsComparision.forEach((item) => {
        item?.metrics?.forEach((metric) => {
          const year = metric.year

          if (!years.includes(year)) {
            years.push(year)
          }
        })
      })
      years.sort((a, b) => a - b)
      this.years = years
    },
    fillMetrics(metricsArray, years, allMonths) {
      const filledMetrics = []
      years.forEach((year) => {
        allMonths.forEach((month) => {
          const existingMetric = metricsArray?.find((metric) => metric.month === month && metric.year === year)
          if (existingMetric) {
            filledMetrics.push(existingMetric)
          } else {
            filledMetrics.push({
              clients: null,
              coupons: null,
              loyalty_average_ticket: null,
              loyalty_coupon_penetration: null,
              loyalty_coupons: null,
              loyalty_revenue: null,
              loyalty_revenue_penetration: null,
              month,
              revenue: null,
              year,
            })
          }
        })
      })
      return filledMetrics
    },
    isUnitDisabled(id) {
      const currentUnit = this.allUnits.find((unit) => unit.id_erp === id)

      if (!currentUnit) return false
      return !currentUnit.active
    },
    getDataToExport() {
      const capitalizeFirstLetter = (string) => {
        return string.charAt(0).toUpperCase() + string.slice(1)
      }

      let monthsNames = Info.months('long', { locale: 'pt-BR' }).map((month) => capitalizeFirstLetter(month))
      let headers = []

      if (this.filterByFranchisee) headers = ['Franqueado', 'CNPJ']
      else if (this.filterByGroups) headers = ['Grupo']
      else headers = ['Loja', 'CNPJ']

      this.years.forEach((year) => {
        headers = headers.concat(monthsNames.map((monthName) => `${monthName}/${year}`))
      })

      const rows = this.items.map((item) => {
        const values = []
        item.metrics.forEach((metric) => {
          values.push(metric[this.selectedIndicator] || 0)
        })
        let row = []
        if (this.filterByGroups) row = [item.name, ...values]
        else row = [item.name, item.cnpj, ...values]

        return row
      })
      const exportData = {
        headers,
        rows,
      }

      return exportData
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .hover-column {
  background-color: $cinza-l90;
}

::v-deep tbody tr:hover {
  background-color: $cinza-l90 !important;
}

::v-deep tbody td:hover {
  background: $cinza-l80 !important;
}

::v-deep .disabled-tr {
  td {
    color: $cinza-l30;
  }
}

.btn-toggle-group {
  ::v-deep button {
    height: 40px !important;
  }

  ::v-deep .v-item--active {
    background: $marca-0-l10;
    color: $branco;
  }

  ::v-deep button:not(.v-item--active) {
    color: $cinza-d10;
    background: $cinza-l90 !important;
  }
}

::v-deep .theme--light.v-btn-toggle:not(.v-btn-toggle--group) .v-btn.v-btn {
  border-color: $cinza-l30 !important;
}

::v-deep .mf-select-units-dialog-input-container {
  width: 180px;
}

::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
  padding: 0 8px;
}

::v-deep td {
  padding: 0 8px;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.ellipsis-container {
  position: relative;
}
.hidden {
  visibility: hidden;
}
.ellipsis-td {
  max-width: calc(100%);
  position: absolute;
  top: 50%;
  left: 0;
  transform: translate(0, -50%);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.indicadores-select {
  max-width: 350px;
}
.year-select {
  max-width: 100px;
}

.no-groups-container {
  padding: 32px;
  background-color: $branco;
  box-shadow: $shadow-card;
  border-radius: 8px;
  .no-groups-content {
    max-width: 800px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 32px;

    p {
      margin: 0;
      max-width: 480px;
      text-align: center;
      font-size: 16px;
      line-height: 120%;
      color: $cinza-d20;
    }
  }
}

.icon-container {
  width: 28px;
  height: 28px;
  background-color: $cinza-l80;
  border-radius: 4px;
}

/* Sticky first column (Header and Body) */
::v-deep .sticky-table .v-data-table__wrapper table tr th:first-child,
.sticky-table .v-data-table__wrapper table tr td:first-child {
  position: sticky;
  left: 0;
  background: white; /* Allow hover effect */
}

/* Sticky second column (Header and Body) */
::v-deep .sticky-table .v-data-table__wrapper table tr th:nth-child(2),
.sticky-table .v-data-table__wrapper table tr td:nth-child(2) {
  position: sticky;
  background: white; /* Allow hover effect */
}

::v-deep .unexpandable-table .v-data-table__wrapper table tr th:nth-child(2),
.unexpandable-table .v-data-table__wrapper table tr td:nth-child(2) {
  left: 250px;
}

::v-deep .expandable-table .v-data-table__wrapper table tr th:nth-child(2),
.expandable-table .v-data-table__wrapper table tr td:nth-child(2) {
  left: 50px;
}

::v-deep .sticky-table .v-data-table__wrapper table tr th:first-child,
::v-deep .sticky-table .v-data-table__wrapper table tr th:nth-child(2) {
  z-index: 1 !important;
}

/* Readd hover background */
::v-deep .sticky-table .v-data-table__wrapper table tr:hover th:nth-child(1),
.sticky-table .v-data-table__wrapper table tr:hover td:nth-child(1),
::v-deep .sticky-table .v-data-table__wrapper table tr:hover th:nth-child(2),
.sticky-table .v-data-table__wrapper table tr:hover td:nth-child(2) {
  background-color: $cinza-l90;
}
</style>
